<template>
  <f7-app v-bind="f7params">
    <MainView v-if="!loginNeeded && !appActivationNeeded" />
    <LoginView v-else />
    <InternalConfigurations />
  </f7-app>
</template>

<script setup>
// @ts-check
import {
  onMounted,
  ref,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useAppConfigStore } from '@/js/stores/appConfig';
import { migrateDataFromVuexToPinia } from '@/js/utils/migrations/migrateDataFromVuexToPinia';
import { migrateParamsFromPinia } from '@erpagroapp/params';
import { migrateSettingsFromPinia, useSettingRef } from '@erpagroapp/settings';
import { useI18n } from 'vue-i18n';
import { useNewStyles } from '@/composables/useNewStyles';

import cordovaApp from '@/js/cordova-app';
import routes from '@/js/routes';
import { f7ready } from 'framework7-vue';
import { isDeviceCordova } from '@/js/utils/platform';
import { IS_DEVELOPMENT } from '@/js/common/constants';
import { DEFAULT_LANGUAGE } from '@erpagroapp/common';
import { setDateLocale } from '@/js/utils/date';

import MainView from '@/views/MainView.vue';
import LoginView from '@/views/LoginView.vue';
import InternalConfigurations from '@/components/internal/InternalConfigurations.vue';

/** @import { Ref } from 'vue' */
/** @import { Framework7Parameters } from 'framework7/types' */

const { initNewStylesHandling } = useNewStyles();
const language = useSettingRef('App.Idioma');
const { locale } = useI18n({ useScope: 'global' });

watch(language, (val) => {
  locale.value = val.Code;
  setDateLocale(val.Code);
}, { immediate: true });

const isCordova = isDeviceCordova();

/**
 * Framework7 parameters for the app.
 * @type {Ref<Framework7Parameters>}
 */
const f7params = ref({
  id: 'es.hispatec.erpagroapp', // App bundle ID
  name: 'ERPagroApp', // App name
  theme: 'auto', // Automatic theme detection

  // App routes
  routes,

  // Register service worker
  serviceWorker: isCordova || IS_DEVELOPMENT ? {} : {
    path: '/service-worker.js',
  },

  // Input settings
  input: {
    scrollIntoViewOnFocus: isCordova,
    scrollIntoViewCentered: isCordova,
  },

  // Cordova Statusbar settings
  statusbar: {
    iosOverlaysWebView: true,
    androidOverlaysWebView: false,
  },
});

const {
  loginNeeded,
  appActivationNeeded,
} = storeToRefs(useAppConfigStore());

/**
 * Initializes Cordova APIs if running in a Cordova environment.
 * @returns {void}
 */
function initiateCordova() {
  f7ready((f7Instance) => {
    if (isCordova) {
      cordovaApp.init(f7Instance);
    }
  });
}

onMounted(() => {
  migrateDataFromVuexToPinia();
  migrateParamsFromPinia();
  migrateSettingsFromPinia();

  // TODO: Temporal. In previous version, `language` could be null
  language.value ||= DEFAULT_LANGUAGE;
  initNewStylesHandling();
  initiateCordova();
});
</script>
